// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Alert, Spinner } from '@loa/ui-components';
import { Card } from '../Card';
import {
  financialPositionApi,
  type AccountSummary,
  type AssetSummary,
  type FinancialPosition,
} from '../../api/financialPositionApi';

interface Props {
  profileId: string;
}

function formatMoney(amount: number | null, currency: string | null): string {
  if (amount === null || amount === undefined) return '—';
  const code = currency ?? 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: code }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${code}`;
  }
}

/**
 * Read-only advisor view of the Financial Position record layer projected from
 * the relationship's Fact-Find: a balance-sheet summary (net worth / assets /
 * liabilities) plus the underlying accounts. Editing happens in the Fact Find
 * tab; this surface is derived. See docs/specs/financial-position-prd.md §10.
 */
export function FinancialPositionTab({ profileId }: Props) {
  const [position, setPosition] = useState<FinancialPosition | null>(null);
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      financialPositionApi.getPosition(profileId),
      financialPositionApi.listAccounts(profileId),
      financialPositionApi.listAssets(profileId),
    ])
      .then(([pos, accs, asts]) => {
        if (cancelled) return;
        setPosition(pos);
        setAccounts(accs);
        setAssets(asts);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the financial position.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) return <Spinner />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!position) return null;

  const assetAccounts = accounts.filter((a) => a.classification === 'ASSET');
  const liabilities = accounts.filter((a) => a.classification === 'LIABILITY');
  const currency = position.currency;
  const nothingCaptured = accounts.length === 0 && assets.length === 0;

  return (
    <div
      className="financial-position-tab"
      data-testid="financial-position-tab"
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Card>
          <div style={{ fontSize: 13, color: '#64748b' }}>Net worth</div>
          <div data-testid="fp-net-worth" style={{ fontSize: 24, fontWeight: 700 }}>
            {formatMoney(position.netWorth, currency)}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 13, color: '#64748b' }}>Total assets</div>
          <div data-testid="fp-total-assets" style={{ fontSize: 24, fontWeight: 700, color: '#16a34a' }}>
            {formatMoney(position.totalAssets, currency)}
          </div>
        </Card>
        <Card>
          <div style={{ fontSize: 13, color: '#64748b' }}>Total liabilities</div>
          <div data-testid="fp-total-liabilities" style={{ fontSize: 24, fontWeight: 700, color: '#dc2626' }}>
            {formatMoney(position.totalLiabilities, currency)}
          </div>
        </Card>
      </div>

      {(position.monthlyIncome !== null || position.monthlyExpenditure !== null) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Card>
            <div style={{ fontSize: 13, color: '#64748b' }}>Monthly income</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              {formatMoney(position.monthlyIncome, currency)}
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 13, color: '#64748b' }}>Monthly outgoings</div>
            <div data-testid="fp-monthly-outgoings" style={{ fontSize: 20, fontWeight: 700 }}>
              {formatMoney(position.monthlyExpenditure, currency)}
            </div>
          </Card>
          <Card>
            <div style={{ fontSize: 13, color: '#64748b' }}>Monthly surplus</div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: (position.monthlySurplus ?? 0) < 0 ? '#dc2626' : '#16a34a',
              }}
            >
              {formatMoney(position.monthlySurplus, currency)}
            </div>
          </Card>
        </div>
      )}

      {nothingCaptured ? (
        <Alert variant="info">
          Nothing captured yet. Accounts, properties and debts appear here once the client completes
          the Savings, Property or Liabilities sections of the Fact Find.
        </Alert>
      ) : (
        <>
          <AccountTable title="Accounts (assets)" rows={assetAccounts} currency={currency} />
          <PropertyTable rows={assets} currency={currency} />
          <AccountTable title="Debts &amp; liabilities" rows={liabilities} currency={currency} />
        </>
      )}
    </div>
  );
}

function PropertyTable({ rows, currency }: { rows: AssetSummary[]; currency: string | null }) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Property</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#64748b' }}>
            <th style={{ padding: '6px 8px' }}>Type</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Value</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Mortgage</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Equity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '6px 8px' }}>
                {a.assetType}
                {a.isJoint ? ' (joint)' : ''}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {formatMoney(a.currentValue, a.currencyCode ?? currency)}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {formatMoney(a.mortgageBalance, a.currencyCode ?? currency)}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>
                {formatMoney(a.equity, a.currencyCode ?? currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function AccountTable({
  title,
  rows,
  currency,
}: {
  title: string;
  rows: AccountSummary[];
  currency: string | null;
}) {
  if (rows.length === 0) return null;
  return (
    <Card>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#64748b' }}>
            <th style={{ padding: '6px 8px' }}>Type</th>
            <th style={{ padding: '6px 8px' }}>Institution</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Balance</th>
            <th style={{ padding: '6px 8px', textAlign: 'right' }}>Monthly</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '6px 8px' }}>
                {a.accountType}
                {a.isJoint ? ' (joint)' : ''}
              </td>
              <td style={{ padding: '6px 8px' }}>{a.institutionName ?? '—'}</td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {formatMoney(a.balance, a.currencyCode ?? currency)}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                {formatMoney(a.monthlyRepayment, a.currencyCode ?? currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
